import { Inject, Injectable } from '@angular/core';
import * as L from 'leaflet';
import { LatLng } from 'leaflet';
import { cloneDeep } from 'lodash';
import { PROJECT_PROVIDER } from '../../core/tokens';
import { ProjectProvider } from '../../core/interfaces';
import { getBoundsForPolygon } from '../utils';
import { LayerService, Region } from '../services/layer.service';
import { ProjectPointsService } from '../project/project-points.service';

/**
 * Service für Freihand-Regionszeichnung auf der Karte.
 * Extrahiert aus MapComponent (Plan 15 Folgearbeit Task 19).
 */
@Injectable({ providedIn: 'root' })
export class FreehandRegionService {

  private readonly FREEHAND_MIN_DISTANCE = 15;

  private map!: L.Map;
  private freehandRegionDrawing = false;
  private freehandRegionPoints: L.LatLng[] = [];
  private freehandRegionPolyline?: L.Polyline;
  private lastFreehandRegionPoint?: L.LatLng;
  private freehandRegionActive = false;

  constructor(
    private ls: LayerService,
    @Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
    private pps: ProjectPointsService,
  ) {}

  init(map: L.Map): void {
    this.map = map;
  }

  destroy(): void {
    this.map = undefined!;
  }

  onFreehandRegionRequested() {
    if (!this.map || this.freehandRegionActive) return;

    this.freehandRegionActive = true;
    this.freehandRegionPoints = [];

    // Eigenes Pane für Zeichnungs-Layer erstellen (on-top)
    if (!this.map.getPane('polygonDrawPane')) {
      const pane = this.map.createPane('polygonDrawPane');
      pane.style.zIndex = '650';
    }

    this.hideAllControls();
    this.map.getContainer().style.cursor = 'crosshair';
    this.map.dragging.disable();

    // Cancel-Button erstellen
    const cancelButton = L.DomUtil.create('button', 'leaflet-bar leaflet-control');
    cancelButton.innerHTML = '✕';
    cancelButton.title = 'Abbrechen';
    cancelButton.style.cssText = 'width: 34px; height: 34px; font-size: 18px; cursor: pointer; background-color: #ff4444; color: white; border: none; position: absolute; top: 10px; right: 10px; z-index: 1000; border-radius: 4px;';
    this.map.getContainer().appendChild(cancelButton);

    const cleanup = () => {
      this.freehandRegionActive = false;
      this.freehandRegionDrawing = false;
      this.freehandRegionPoints = [];
      this.lastFreehandRegionPoint = undefined;

      if (this.freehandRegionPolyline) {
        this.freehandRegionPolyline.remove();
        this.freehandRegionPolyline = undefined;
      }

      this.showAllControls();
      this.map.getContainer().style.cursor = '';
      this.map.dragging.enable();
      cancelButton.remove();

      this.map.off('mousedown', onMouseDown);
      this.map.off('mousemove', onMouseMove);
      this.map.off('mouseup', onMouseUp);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
    };

    const container = this.map.getContainer();

    const onMouseDown = (e: L.LeafletMouseEvent) => {
      this.freehandRegionDrawing = true;
      this.freehandRegionPoints = [e.latlng];
      this.lastFreehandRegionPoint = e.latlng;

      this.freehandRegionPolyline = L.polyline([e.latlng], {
        color: '#3388ff',
        weight: 3,
        dashArray: '5, 5',
        pane: 'polygonDrawPane'
      }).addTo(this.map);
    };

    const onMouseMove = (e: L.LeafletMouseEvent) => {
      if (!this.freehandRegionDrawing || !this.lastFreehandRegionPoint || !this.freehandRegionPolyline) return;

      const lastContainerPoint = this.map.latLngToContainerPoint(this.lastFreehandRegionPoint);
      const distance = e.containerPoint.distanceTo(lastContainerPoint);

      if (distance >= this.FREEHAND_MIN_DISTANCE) {
        this.freehandRegionPoints.push(e.latlng);
        this.freehandRegionPolyline.addLatLng(e.latlng);
        this.lastFreehandRegionPoint = e.latlng;
      }
    };

    const onMouseUp = () => {
      if (!this.freehandRegionDrawing) return;
      this.freehandRegionDrawing = false;

      if (this.freehandRegionPoints.length >= 3) {
        this.createRegionFromPolygon(this.freehandRegionPoints);
      }

      cleanup();
      this.ls.regionDrawingCompleted.emit();
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      e.preventDefault();

      const touch = e.touches[0];
      const latlng = this.map.containerPointToLatLng(
        L.point(touch.clientX - container.getBoundingClientRect().left, touch.clientY - container.getBoundingClientRect().top)
      );

      this.freehandRegionDrawing = true;
      this.freehandRegionPoints = [latlng];
      this.lastFreehandRegionPoint = latlng;

      this.freehandRegionPolyline = L.polyline([latlng], {
        color: '#3388ff',
        weight: 3,
        dashArray: '5, 5',
        pane: 'polygonDrawPane'
      }).addTo(this.map);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!this.freehandRegionDrawing || e.touches.length !== 1) return;
      e.preventDefault();

      const touch = e.touches[0];
      const rect = container.getBoundingClientRect();
      const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
      const latlng = this.map.containerPointToLatLng(containerPoint);

      if (!this.lastFreehandRegionPoint || !this.freehandRegionPolyline) return;

      const lastContainerPoint = this.map.latLngToContainerPoint(this.lastFreehandRegionPoint);
      const distance = containerPoint.distanceTo(lastContainerPoint);

      if (distance >= this.FREEHAND_MIN_DISTANCE) {
        this.freehandRegionPoints.push(latlng);
        this.freehandRegionPolyline.addLatLng(latlng);
        this.lastFreehandRegionPoint = latlng;
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!this.freehandRegionDrawing) return;
      e.preventDefault();
      this.freehandRegionDrawing = false;

      if (this.freehandRegionPoints.length >= 3) {
        this.createRegionFromPolygon(this.freehandRegionPoints);
      }

      cleanup();
      this.ls.regionDrawingCompleted.emit();
    };

    // Cancel-Button Event
    L.DomEvent.on(cancelButton, 'click', () => {
      cleanup();
      this.ls.regionDrawingCompleted.emit();
    });

    // Event-Listener registrieren
    this.map.on('mousedown', onMouseDown);
    this.map.on('mousemove', onMouseMove);
    this.map.on('mouseup', onMouseUp);
    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
  }

  private createRegionFromPolygon(polygon: L.LatLng[]) {
    const regions = this.ls.regions();
    const newIndex = regions.regions.length;
    const newRegion: Region = {
      type: 'polygon',
      bounds: getBoundsForPolygon(polygon),
      polygon: polygon.map(p => new LatLng(p.lat, p.lng))
    };
    regions.regions.push(newRegion);
    const updatedRegions = cloneDeep(regions);
    this.ls.regions.update(() => updatedRegions);
    this.ps.setProjectValue('regions', updatedRegions);
    this.ls.regionsChanged.emit();
    this.pps.activeRegionIndices.update(current => [...current, newIndex]);
  }

  private hideAllControls() {
    const mapContainer = this.map.getContainer();
    const controls = mapContainer.querySelectorAll('.leaflet-control');
    controls.forEach((control) => {
      const htmlControl = control as HTMLElement;
      if (!htmlControl.dataset['originalDisplay']) {
        htmlControl.dataset['originalDisplay'] = htmlControl.style.display || '';
      }
      htmlControl.style.display = 'none';
    });
  }

  private showAllControls() {
    const mapContainer = this.map.getContainer();
    const controls = mapContainer.querySelectorAll('.leaflet-control');
    controls.forEach((control) => {
      const htmlControl = control as HTMLElement;
      htmlControl.style.display = htmlControl.dataset['originalDisplay'] || '';
      delete htmlControl.dataset['originalDisplay'];
    });
  }
}
